import React, { useState, useEffect } from "react";
import styled from "styled-components";

import CustomModal from "shared/components/CustomModal";
import * as S from "@styled";
import { IndeterminateCheckbox } from "shared/components/Table";
import { Utilities } from "shared/utilities";
import colors from "shared/styles/colors";

import { AppContext } from "../../App";
import { ProcessTemplateAppReducerState, ProcessesUsingTemplateType } from "processTemplates/model/AppState";
import { PROCESS_TEMPLATE_BASE_URL, getProcessTemplates, handleApiError, setUsedInModalOpen } from "processTemplates/actions/AppActions";
import ProcessLinkList from "../ProcessLinkList";
import { SET_LOADING } from "processTemplates/reducers/AppReducer";
import { ProcessUsingTemplate } from "processTemplates/model/ProcessTemplate";
import { getProcessListLink } from "processTemplates/model/List";

const UsedInModal: React.FC = () => {
    const { state, dispatch } = React.useContext<ProcessTemplateAppReducerState>(AppContext);
    const [processesUsingTemplate, setProcessesUsingTemplate] = useState<ProcessUsingTemplate[]>();

    useEffect(() => {
        (async () => {
            if (state.usedInModalState) {
                const { processTemplate, mode } = state.usedInModalState;
                try {
                    dispatch({ type: SET_LOADING, loading: true });
                    const processes = await getProcessesUsingTemplate(processTemplate.id, mode);
                    setProcessesUsingTemplate(processes);
                    dispatch({ type: SET_LOADING, loading: false });
                } catch (e) {
                    handleApiError(dispatch, e);
                }
            } else {
                setProcessesUsingTemplate(undefined);
            }
        })();
    }, [state.usedInModalState]);

    const isOpen = () => {
        return state.usedInModalState !== undefined;
    };

    const handleClose = () => {
        setUsedInModalOpen(dispatch, undefined);
    };

    const getTitle = () => {
        switch (state.usedInModalState?.mode) {
            case ProcessesUsingTemplateType.Current:
                return 'Processes using current Template version';
            case ProcessesUsingTemplateType.Older:
                return 'Processes using old Template version';
            case ProcessesUsingTemplateType.Deviated:
                return 'Processes deviated from Template';
            default:
                return 'Processes using Template';
        }
    }

    return (
        <CustomModal
            label="template-used-in-modal"
            size="medium"
            onClose={handleClose}
            shouldCloseOnOverlayClick={true}
            show={isOpen()}
            modalTitle={<h2>{getTitle()}</h2>}
        >
            <>
                {processesUsingTemplate && state.usedInModalState?.mode === ProcessesUsingTemplateType.Current && (
                    <CurrentVersionUsedInModalContent prosessesUsingTemplate={processesUsingTemplate} />
                )}

                {processesUsingTemplate && state.usedInModalState?.mode === ProcessesUsingTemplateType.Older && (
                    <UpdateUsedInProcessesModalContent
                        prosessesUsingTemplate={processesUsingTemplate}
                        warning="Updating several processes at once can take a while."
                    />
                )}

                {processesUsingTemplate && state.usedInModalState?.mode === ProcessesUsingTemplateType.Deviated && (
                    <UpdateUsedInProcessesModalContent
                        prosessesUsingTemplate={processesUsingTemplate}
                        warning="Update will overwrite any changes made to deviated Processes. Updating several processes at once can take a while."
                    />
                )}
            </>
        </CustomModal>
    );
};

export default UsedInModal;

interface ICurrentVersionUsedInModalContentProps {
    prosessesUsingTemplate: ProcessUsingTemplate[];
}

const CurrentVersionUsedInModalContent: React.FC<ICurrentVersionUsedInModalContentProps> = ({ prosessesUsingTemplate }) => {
    const linkListItems = prosessesUsingTemplate.map((p) => ({ name: p.name, href: getProcessListLink(p) }));

    return (
        <>
            <h3>
                Process
            </h3>

            <ProcessLinkList prosessLinks={linkListItems} />

            <S.ButtonGroup $justifyContentEnd>
                <CloseUsedInModalButton />
            </S.ButtonGroup>
        </>
    );
};

interface IUpdateUsedInProcessesModalContentProps {
    prosessesUsingTemplate: ProcessUsingTemplate[];
    warning: string;
}

const UpdateUsedInProcessesModalContent: React.FC<IUpdateUsedInProcessesModalContentProps> = ({ prosessesUsingTemplate, warning }) => {
    const { state, dispatch } = React.useContext<ProcessTemplateAppReducerState>(AppContext);
    const [selectedProcessIds, setSelectedProcessIds] = useState(new Set<number>());
    const MAX_UPDATE_BATCH_SIZE = 20;

    const isProcessSelectd = (processId: number) => selectedProcessIds.has(processId);

    const handleToggleSelectProcess = (processId: number) => {
        const nextProcessIds = new Set(selectedProcessIds);
        if (nextProcessIds.has(processId)) {
            nextProcessIds.delete(processId);
        } else {
            nextProcessIds.add(processId);
        }

        if (nextProcessIds.size <= MAX_UPDATE_BATCH_SIZE) {
            setSelectedProcessIds(nextProcessIds);
        } else {
            alert(`Maximum batch process update limit is ${MAX_UPDATE_BATCH_SIZE}.`);
        }
    };

    const iSomeProcessSeleted = () => 0 < selectedProcessIds.size;

    const handlePostUpdateData = async (processIds: number[]) => {
        try {
            dispatch({ type: SET_LOADING, loading: true });
            await Utilities.postJsonAndParseResponse(`${PROCESS_TEMPLATE_BASE_URL}/${state.usedInModalState?.processTemplate.id}/updateProcessesToLatestVersion`, { processIds });
            Utilities.toastSuccess(`Process${processIds.length > 0 ? 'es' : ''} updated`);
            dispatch({ type: SET_LOADING, loading: false });
            setUsedInModalOpen(dispatch, undefined);
            getProcessTemplates(dispatch);
        } catch (e) {
            handleApiError(dispatch, e);
        }
    };

    const handleUpdateSelectedButtonClick = () => {
        handlePostUpdateData([...selectedProcessIds]);
    };

    const canUpdateAll = () => prosessesUsingTemplate.length <= MAX_UPDATE_BATCH_SIZE;

    const handleUpdateAllButtonClick = () => {
        if (canUpdateAll()) {
            handlePostUpdateData(prosessesUsingTemplate.map((p) => p.id));
        }
    };

    return (
        <>
            <S.StyledTable
                gridTemplateColumns="60px 4fr 2fr"
                rowHeight={70}
            >
                <thead>
                    <tr>
                        <th>
                            <S.SROnly>
                                Select Process
                            </S.SROnly>
                        </th>
                        <th>
                            Process
                        </th>
                        <th>
                            Template version
                        </th>
                    </tr>
                </thead>

                <tbody>
                    {prosessesUsingTemplate.map((p, i) => (
                        <tr key={i}>
                            <td>
                                <IndeterminateCheckbox
                                    {...{
                                        id: `select-row-${p.id}-selector`,
                                        checked: isProcessSelectd(p.id),
                                        indeterminate: iSomeProcessSeleted(),
                                        onChange: () => handleToggleSelectProcess(p.id),
                                    }}
                                />
                            </td>
                            <td>
                                <S.A
                                    href={getProcessListLink(p)}
                                    title="View"
                                    variant={S.LinkTextColor.black}
                                    target="__blank"
                                    style={{ textDecoration: 'underline' }}
                                >
                                    {p.name}
                                </S.A>
                            </td>
                            <td>
                                {`${p.templateMajorVersion}.${p.templateMinorVersion}.${p.templateVersion}`}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </S.StyledTable>

            <StyledBottomContainer>
                <div>
                    <i className="ci-warning" />
                    <p>{warning}</p>
                </div>

                <S.ButtonGroup $justifyContentEnd gap={35} margin="0">
                    <CloseUsedInModalButton />

                    <S.Button
                        id="update-selected-button"
                        disabled={!iSomeProcessSeleted()}
                        onClick={handleUpdateSelectedButtonClick}
                        type="button"
                    >
                        Update selected
                    </S.Button>

                    {canUpdateAll() && (
                        <S.Button
                            id="update-all-button"
                            onClick={handleUpdateAllButtonClick}
                            type="button"
                        >
                            Update all
                        </S.Button>
                    )}
                </S.ButtonGroup>
            </StyledBottomContainer>
        </>
    );
};

const StyledBottomContainer = styled.div`
    margin-top: 30px;
    display: grid;
    grid-template-columns: auto max-content;
    gap: 40px;
    align-items: center;

    div:first-child {
        padding-left: 16px;
        display: flex;
        gap: 4px;

        i {
            color: ${colors.orangePrimary};
            font-size: 24px;
        }

        p {
            margin: 0;
            font-family: 'Gilroy-Medium';
            font-size: 18px;
        }
    }
`;

const CloseUsedInModalButton = () => {
    const { dispatch } = React.useContext<ProcessTemplateAppReducerState>(AppContext);

    const handleClick = () => setUsedInModalOpen(dispatch, undefined);

    return (
        <S.Button
            id="close-used-in-modal-button"
            onClick={handleClick}
            type="button"
        >
            Close
        </S.Button>
    );
};

export const getProcessesUsingTemplate = async (templateId: number, processType: ProcessesUsingTemplateType): Promise<ProcessUsingTemplate[]> => {
    return await Utilities.getFetch(`${PROCESS_TEMPLATE_BASE_URL}/${templateId}/processes`, { processType: String(processType) });
};